import './TestConfigSection.css';
import React, { useState, useEffect, useCallback } from 'react';
import { loadTestConfigs, saveTestConfig, setActiveConfig, EMPTY_CONFIG } from '../../logic/testConfigLogic';
import { getQuestionCategories } from '../../../shared/services/apiService';
import { ActIcon } from '../../components/AdminIcons';

export default function TestConfigSection({ notify, can }) {
  const [configs,     setConfigs]     = useState([]);
  const [cfgForm,     setCfgForm]     = useState(EMPTY_CONFIG);
  const [cfgEditId,   setCfgEditId]   = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [busy,        setBusy]        = useState({});
  const [categories,  setCategories]  = useState([]);
  const [catInput,    setCatInput]    = useState({ category: '', count: 1 });

  const isBusy   = key => !!busy[key];
  const withBusy = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const configs = await loadTestConfigs();
      setConfigs(configs);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load configs', true); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getQuestionCategories()
      .then(res => setCategories(res.data.data.categories || []))
      .catch(() => {});
  }, []);

  // ── Category distribution helpers ──────────────────────────────
  const catDist = cfgForm.category_distribution || [];

  const addCatRow = () => {
    const cat   = catInput.category.trim();
    const count = parseInt(catInput.count) || 1;
    if (!cat) { notify('Select a category first', true); return; }
    if (catDist.find(r => r.category === cat)) { notify('Category already added', true); return; }
    const newDist = [...catDist, { category: cat, count }];
    const total   = newDist.reduce((s, r) => s + r.count, 0);
    setCfgForm(f => ({ ...f, category_distribution: newDist, total_questions: total }));
    setCatInput({ category: '', count: 1 });
  };

  const updateCatCount = (cat, value) => {
    const newDist = catDist.map(r => r.category === cat ? { ...r, count: parseInt(value) || 1 } : r);
    const total   = newDist.reduce((s, r) => s + r.count, 0);
    setCfgForm(f => ({ ...f, category_distribution: newDist, total_questions: total }));
  };

  const removeCatRow = (cat) => {
    const newDist = catDist.filter(r => r.category !== cat);
    const total   = newDist.reduce((s, r) => s + r.count, 0) || cfgForm.total_questions;
    setCfgForm(f => ({ ...f, category_distribution: newDist, total_questions: total }));
  };

  // ────────────────────────────────────────────────────────────────
  const handleSaveConfig = async e => {
    e.preventDefault();
    await withBusy('save-config', async () => {
      try {
        const action = await saveTestConfig(cfgForm, cfgEditId);
        notify(action === 'updated' ? 'Config updated' : 'Config created');
        if (action === 'updated') setCfgEditId(null);
        setCfgForm(EMPTY_CONFIG);
        load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const activateConfig = async (id, title) => {
    await withBusy(`activate-${id}`, async () => {
      try {
        await setActiveConfig(id);
        notify(`"${title}" is now the active test config`); load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const startEdit = (c) => {
    setCfgEditId(c.id);
    setCfgForm({
      title:                 c.title,
      description:           c.description || '',
      total_questions:       c.total_questions,
      duration_minutes:      c.duration_minutes,
      passing_marks:         c.passing_marks,
      max_violations:        c.max_violations,
      randomize_questions:   !!c.randomize_questions,
      category_distribution: Array.isArray(c.category_distribution) ? c.category_distribution : [],
    });
  };

  const availableCats = categories.filter(c => !catDist.find(r => r.category === c));

  return (
    <div className="fade-up">
      {can('testconfig','edit') && (
        <div className="form-section" style={{width:'100%',marginBottom:'1.5rem'}}>
          <h3>{cfgEditId ? 'Edit Config' : 'New Test Configuration'}</h3>
          <form onSubmit={handleSaveConfig}>
            <div className="form-grid" style={{marginBottom:'1rem'}}>
              <div className="input-group" style={{gridColumn:'1/-1'}}>
                <label>Title *</label>
                <input className="input-field" placeholder="General Aptitude Test"
                  value={cfgForm.title} onChange={e=>setCfgForm(f=>({...f,title:e.target.value}))} required/>
              </div>
              <div className="input-group" style={{gridColumn:'1/-1'}}>
                <label>Description</label>
                <textarea className="input-field" rows="2" placeholder="Optional description"
                  value={cfgForm.description} onChange={e=>setCfgForm(f=>({...f,description:e.target.value}))}/>
              </div>
              <div className="input-group">
                <label>Total Questions *</label>
                <input type="number" min="1" className="input-field"
                  value={cfgForm.total_questions}
                  onChange={e=>setCfgForm(f=>({...f,total_questions:parseInt(e.target.value)||10}))}
                  readOnly={catDist.length > 0}
                  style={catDist.length > 0 ? {opacity:0.55,cursor:'not-allowed'} : {}}
                  title={catDist.length > 0 ? 'Auto-calculated from category distribution' : ''}
                />
              </div>
              <div className="input-group">
                <label>Duration (minutes) *</label>
                <input type="number" min="1" className="input-field"
                  value={cfgForm.duration_minutes} onChange={e=>setCfgForm(f=>({...f,duration_minutes:parseInt(e.target.value)||30}))}/>
              </div>
              <div className="input-group">
                <label>Passing Marks (%)</label>
                <input type="number" min="0" max="100" className="input-field"
                  value={cfgForm.passing_marks} onChange={e=>setCfgForm(f=>({...f,passing_marks:parseInt(e.target.value)||60}))}/>
              </div>
              <div className="input-group">
                <label>Max Violations</label>
                <input type="number" min="1" className="input-field"
                  value={cfgForm.max_violations} onChange={e=>setCfgForm(f=>({...f,max_violations:parseInt(e.target.value)||3}))}/>
              </div>
              <div className="input-group" style={{gridColumn:'1/-1'}}>
                <label style={{flexDirection:'row',gap:'0.5rem',alignItems:'center',cursor:'pointer'}}>
                  <input type="checkbox" checked={cfgForm.randomize_questions}
                    onChange={e=>setCfgForm(f=>({...f,randomize_questions:e.target.checked}))}
                    style={{width:'16px',height:'16px',accentColor:'var(--accent)'}}/>
                  Randomize question order for each candidate
                </label>
              </div>
            </div>

            {/* ── Category Distribution ── */}
            <div className="tcc-dist-section">
              <div className="tcc-dist-header">
                <div className="tcc-dist-title">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  Category-wise Distribution
                </div>
                <span className="tcc-dist-hint">
                  {catDist.length === 0
                    ? 'Optional — leave empty to draw from all categories randomly'
                    : `${catDist.reduce((s,r)=>s+r.count,0)} questions total across ${catDist.length} categor${catDist.length===1?'y':'ies'}`}
                </span>
              </div>

              {catDist.length > 0 && (
                <div className="tcc-dist-rows">
                  {catDist.map(row => (
                    <div key={row.category} className="tcc-dist-row">
                      <span className="tcc-dist-cat">{row.category}</span>
                      <div className="tcc-dist-count-wrap">
                        <button type="button" className="tcc-dist-stepper"
                          onClick={() => updateCatCount(row.category, row.count - 1)}
                          disabled={row.count <= 1}>−</button>
                        <input
                          type="number" min="1" className="tcc-dist-count-input"
                          value={row.count}
                          onChange={e => updateCatCount(row.category, e.target.value)}
                        />
                        <button type="button" className="tcc-dist-stepper"
                          onClick={() => updateCatCount(row.category, row.count + 1)}>+</button>
                      </div>
                      <span className="tcc-dist-qlabel">question{row.count !== 1 ? 's' : ''}</span>
                      <button type="button" className="tcc-dist-remove" title="Remove"
                        onClick={() => removeCatRow(row.category)}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="tcc-dist-add-row">
                <div className="tcc-dist-select-wrap">
                  <select className="input-field tcc-dist-select"
                    value={catInput.category}
                    onChange={e => setCatInput(i => ({ ...i, category: e.target.value }))}>
                    <option value="">Select category…</option>
                    {availableCats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <input
                  type="number" min="1" className="input-field tcc-dist-count-new"
                  placeholder="Count" value={catInput.count}
                  onChange={e => setCatInput(i => ({ ...i, count: parseInt(e.target.value) || 1 }))}
                />
                <button type="button" className="btn btn-outline btn-sm tcc-dist-add-btn"
                  onClick={addCatRow} disabled={!catInput.category}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Category
                </button>
              </div>
            </div>

            <div className="form-actions" style={{marginTop:'1rem'}}>
              <button type="submit" className="btn btn-primary" disabled={isBusy('save-config')}>
                {isBusy('save-config') ? <><span className="btn-spinner"/>{cfgEditId ? 'Updating…' : 'Creating…'}</> : (cfgEditId ? '💾 Update Config' : '➕ Create Config')}
              </button>
              {cfgEditId && <button type="button" className="btn btn-outline" onClick={()=>{setCfgEditId(null);setCfgForm(EMPTY_CONFIG);}}>Cancel</button>}
            </div>
          </form>
        </div>
      )}
      {loading ? <div className="admin-loading"><div className="spinner"/></div>
      : configs.length===0 ? <div className="empty-state"><span>⚙️</span><p>No configs yet.</p></div>
      : <div className="results-table-wrap"><table className="results-table">
          <thead><tr><th>Title</th><th>Questions</th><th>Distribution</th><th>Duration</th><th>Pass %</th><th>Max Violations</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{configs.map(c=>(
            <tr key={c.id}>
              <td><div><div className="cname">{c.title}</div>{c.description&&<div className="cemail">{c.description}</div>}</div></td>
              <td><code>{c.total_questions}</code></td>
              <td>
                {Array.isArray(c.category_distribution) && c.category_distribution.length > 0
                  ? <div className="tcc-dist-badges">
                      {c.category_distribution.map(r => (
                        <span key={r.category} className="tcc-dist-badge">{r.category}: {r.count}</span>
                      ))}
                    </div>
                  : <span style={{color:'var(--muted)',fontSize:'0.78rem'}}>All categories</span>
                }
              </td>
              <td><code>{c.duration_minutes} min</code></td>
              <td><code>{c.passing_marks}%</code></td>
              <td><code>{c.max_violations}</code></td>
              <td><span className={`badge ${c.is_active?'badge-green':'badge-purple'}`}>{c.is_active?'Active':'Inactive'}</span></td>
              <td><div className="action-btns">
                {can('testconfig','edit') && <button className="act-btn info" title="Edit" onClick={()=>startEdit(c)}><ActIcon type="edit"/></button>}
                {can('testconfig','edit') && !c.is_active && (
                  <button className="act-btn success" title="Set as Active" disabled={isBusy(`activate-${c.id}`)} onClick={()=>activateConfig(c.id,c.title)}>
                    {isBusy(`activate-${c.id}`) ? <span className="act-spinner"/> : <ActIcon type="activate"/>}
                  </button>
                )}
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>}
    </div>
  );
}
